<template>
  <component
    :is="tag"
    v-if="isBackgroundImage"
    v-lazyload="lazyloadOptions"
    :style="backgroundImgStyle"
    v-on="$listeners"
  >
    <slot></slot>
  </component>
  <img v-else v-lazyload="lazyloadOptions" :src="imgSrcAttributeValue" v-on="$listeners" />
</template>

<script>
/**
 * This component is a wrapper for the ContentfulImagepathOptimizer.
 * It renders images as html img tag or css background image (using isBackgroundImage and tag prop).
 *
 * @see ContentfulImagepathOptimizer (front/assets/javascripts/on/utility/contentful-imagepath-optimizer.js)
 */

import { mapGetters } from 'vuex'
import { isWebpImageSupported } from '@/helpers/browser-support'

const CONTENTFUL_IMAGE_FIT = On.CONTENTFUL_IMAGE_FIT
const CONTENTFUL_IMAGE_FORMAT = On.CONTENTFUL_IMAGE_FORMAT

export { CONTENTFUL_IMAGE_FIT, CONTENTFUL_IMAGE_FORMAT }

export default {
  name: 'ImageContentful',
  props: {
    src: {
      type: String,
      required: true,
    },
    srcMobile: {
      type: String,
      required: false,
    },
    containerSelector: {
      type: String,
      required: false,
    },
    imageMaxWidth: {
      type: Number,
      required: false,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Overwrite the format specified in the options if webp is supported by the browser
     */
    isUsingWebpIfSupported: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines wether image is rendered as html img tag or css background image.
     * Use tag prop to specify the tag name of the background image.
     */
    isBackgroundImage: {
      type: Boolean,
      default: false,
    },
    /**
     * Determines tag name if image is rendered as background (using isBackgroundImage prop).
     */
    tag: {
      type: String,
      default: 'div',
    },
    /**
     * Set lazyload to "true" if you want to enable lazy loading using the default options.
     * Use an object to enable lazy loading while overwriting some of the default options.
     * For valid options see lazyload directive. Src option will be overwritten by optimizedSrc.
     */
    lazyload: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      optimizedSrc: '',
    }
  },

  computed: {
    ...mapGetters({
      viewportWidth: 'viewport/width',
    }),

    lazyloadOptions() {
      if (!this.lazyload) return false

      const options = this.lazyload === true ? {} : this.lazyload
      return { ...options, src: this.optimizedSrc }
    },

    backgroundImgStyle() {
      return this.lazyload ? false : `background-image: url(${this.optimizedSrc})`
    },

    imgSrcAttributeValue() {
      return this.lazyload ? false : this.optimizedSrc
    },
  },

  watch: {
    src: 'optimizeSrc',
    viewportWidth: 'optimizeSrc',
    imageMaxWidth: 'optimizeSrc',
  },

  created() {
    if (this.isUsingWebpIfSupported) isWebpImageSupported() // cache response as soon as possible
  },

  mounted() {
    this.optimizeSrc()
  },

  methods: {
    async optimizeSrc() {
      let isUsingWebpFormat = false
      if (this.isUsingWebpIfSupported) {
        /**
         * Use isWebpImageSupported js helper instead of <picture> and <source> tags because it
         * would have complicate the logic quite a bit:
         * - Always need to compute the `optimizedSrc` for both webp and fallback image format
         * - Need to keep calling isWebpImageSupported() anyway for background image
         * - Lazyload directive wouldn't work because we wouldn't have one src to provide
         */
        isUsingWebpFormat = await isWebpImageSupported()
      }
      const opts = {
        ...this.options,
        format: isUsingWebpFormat ? CONTENTFUL_IMAGE_FORMAT.webp : this.options.format,
        container: this.containerSelector,
        desktopSrc: this.src,
        mobileSrc: this.srcMobile,
      }
      if (this.imageMaxWidth) opts.imageMaxWidth = this.imageMaxWidth
      this.optimizedSrc = On.optimizeContentfulPath(opts)
    },
  },
}
</script>
